// Imports
import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";
import Select, { components } from "react-select";
import { createPortal } from 'react-dom';

// Constants
const BABY_BLUE = "#89CFF0";
const REG_BLUE = "#348FD9";
const DEEP_BLUE = "#222b36";
const NEON_GREEN = "#74ee15"; // neon green
//const NEON_YELLOW = "#ffe700";
const QUIT_RED = "#E3242B";
const DEFAULT_CAMERA_POS = [0, 0, 40];

const DEFAULT_SECTION_COLORS = [
  "#4dd7ee", // neon adolescent blue
  "#ffe700", // neon yellow
  "#f000ff", // neon pink
  "#c493ff", // light purple
  "#74ee15", // neon green
  "#ffaa00", // neon orange
  "#fe0000", // neon red
  "#00ffbc", // fluorescent green
  "#001eff", // neon indigo
  "#aaff00", // neon lime
];

// Helper function to filter out "In English" from genres
const excludeBadGenres = (genres) => {
  if (!genres || !Array.isArray(genres)) return genres;
  return genres.filter(genre => genre.toLowerCase() !== 'in english');
};

// Helper function to get wiki frequency label
const getWikiFrequencyLabel = (freq) => {
  if (freq > 1_000_000) return "common";
  if (freq > 100_000) return "uncommon";
  if (freq > 10_000) return "rare";
  if (freq > 1_000) return "very rare";
  if (freq > 100) return "epic";
  return "legendary";
};

// Adding a browser size listener for dynamic browser resizing
function useWindowSize() {
  const getSize = () => {
    const styles = getComputedStyle(document.documentElement);
    let availableWidth = parseInt(styles.getPropertyValue('--available-width'));
    let availableHeight = parseInt(styles.getPropertyValue('--available-height'));
    
    if (isNaN(availableWidth) || availableWidth === 0) {
      availableWidth = window.innerWidth;
    }
    if (isNaN(availableHeight) || availableHeight === 0) {
      availableHeight = window.innerHeight;
    }

    return {
      width: window.innerWidth,
      height: window.innerHeight,
      dwidth: availableWidth,
      dheight: availableHeight
    };
  };

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      setWindowSize(getSize());
    }, 50);

    const handleResize = () => {
      setTimeout(() => {
        setWindowSize(getSize());
      }, 10);
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return windowSize;
}

// Helper: colored circle for dropdown options
function ColorCircle({ color }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: 12,
        backgroundColor: color,
        marginRight: 0,
        verticalAlign: "middle",
        border: "1px solid #00000033"
      }}
    />
  );
}

// Dropdown for choosing section color
const colorOptions = [
  { value: "", label: <span className="transparent-label" style={{ fontSize: 19.6 }}>∅</span> }, // Transparent
  ...DEFAULT_SECTION_COLORS.map(c => ({ value: c, label: <ColorCircle color={c} /> }))
];

// Dropdown for song sections
function SectionColorDropdown({ section, color, onChange, disabled = false }) {
  const selectedOption = colorOptions.find(o => o.value === color);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: '75px',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer'
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto !important',
      touchAction: 'pan-y !important',
      WebkitOverflowScrolling: 'touch',
      position: 'absolute',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto',
    }),
  };

  return (
    <Select
      key={color || ''}
      value={selectedOption}
      onChange={(selected) => !disabled && onChange(section, selected.value)}
      options={colorOptions}
      isSearchable={false}
      isDisabled={disabled}
      classNames={{
        control: () => "dropdown-control",
        valueContainer: () => "dropdown-value-container",
        indicatorsContainer: () => "dropdown-indicators-container",
        dropdownIndicator: () => "dropdown-dropdown-indicator",
        menu: () => "dropdown-menu",
        option: () => "dropdown-option",
        singleValue: () => "dropdown-single-value"
      }}
      components={{
        IndicatorSeparator: () => null,
        Menu: (props) => <components.Menu {...props} id="color-dropdown-menu" />
      }}
      menuShouldBlockScroll={false}
      captureMenuScroll={false}
      menuPosition="absolute"
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      styles={{
        ...customStyles,
        menuList: (provided) => ({
          ...provided,
          maxHeight: '80px',
          overflowY: 'auto',   
        }),
      }}
    />
  );
}

function MasterColorDropdown({ expandedSectionsColors = [], color, onChange, disabled = false, isMysterySong = false, discoveredSections = [] }) {
  const uniqueColors = new Set(expandedSectionsColors.filter(c => c));
  const isUniform = uniqueColors.size === 1;
  const uniformColor = isUniform ? [...uniqueColors][0] : null;
  const isAllTransparent = expandedSectionsColors.every(c => !c);
  const showRainbow = !isUniform && !isAllTransparent;

  const allSectionsDiscovered = !isMysterySong || discoveredSections.length === expandedSectionsColors.length;
  const shouldDisable = disabled || (isMysterySong && !allSectionsDiscovered);

  const rainbowOption = { value: 'mixed', label: <span style={{ fontSize: 16 }}>🍭</span> };

  const selectedOption = showRainbow
    ? rainbowOption
    : colorOptions.find(o => o.value === (isAllTransparent ? "" : uniformColor));

  const indicatorClass = showRainbow ? "rainbow" : (isAllTransparent ? "transparent" : "");

  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: '75px',
      opacity: shouldDisable ? 0.5 : 1,
      cursor: shouldDisable ? 'not-allowed' : 'pointer'
    }),
    menu: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto !important',
      touchAction: 'pan-y !important',
      WebkitOverflowScrolling: 'touch',
      position: 'absolute',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '80px',
      overflowY: 'auto',
    }),
  };

  return (
    <Select
      key={color || ''}
      value={selectedOption} 
      onChange={selected => !shouldDisable && onChange(selected.value)}
      options={colorOptions}
      isSearchable={false}
      isDisabled={shouldDisable}
      classNames={{
        control: () => "dropdown-control",
        valueContainer: () => "dropdown-value-container",
        indicatorsContainer: () => "dropdown-indicators-container",
        dropdownIndicator: () => `dropdown-dropdown-indicator ${indicatorClass}`,
        menu: () => "dropdown-menu",
        option: () => "dropdown-option",
        singleValue: () => "dropdown-single-value"
      }}
      components={{
        IndicatorSeparator: () => null,
        Menu: (props) => <components.Menu {...props} id="color-dropdown-menu" />
      }}
      menuShouldBlockScroll={false}
      captureMenuScroll={false}
      menuPosition="absolute"
      menuPlacement="auto"
      menuShouldScrollIntoView={false}
      styles={{
        ...customStyles,
        menuList: (provided) => ({
          ...provided,
          maxHeight: '80px',
          overflowY: 'auto',   
        }),
      }}
    />
  );
}

// Single song entry in the dashboard
function SongEntry({
  song,
  isExpanded,
  toggleExpanded,
  onSectionColorChange,
  onMasterColorChange,
  onDelete,
  expandedSectionsColors,
  discoveredAttributes,
  mysterySongId,
  isGameWon
}) {
  const isMysterySong = song.id === mysterySongId;
  
  const sections = Object.keys(song.sections || {});

  const getYearFromDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.getFullYear();
    } catch (error) {
      return null;
    }
  };

  return (
    <div style={{ marginBottom: 20, borderBottom: "1px solid #555", paddingBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div 
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            WebkitTapHighlightColor: 'transparent'
          }} 
          onClick={toggleExpanded}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleExpanded();
            }
          }}
          tabIndex={0}
          role="button"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse song details" : "Expand song details"}
        >
          <span style={{ marginLeft: 10, marginRight: 16, fontSize: 20, color: "#ccc" }}>
            {isExpanded ? "📂" : "📁"}
          </span>
          <div style={{ fontSize: 20, color: "#fff", marginRight: 16 }}>
            <strong>
              {isMysterySong && !isGameWon && !discoveredAttributes?.name ? "???" : song.name}
            </strong>
            <div style={{ fontSize: 16, color: "#ccc" }}>
              {isMysterySong && !isGameWon && !discoveredAttributes?.artist ? "???" : song.artist}
            </div>
            <div style={{ fontSize: 12, color: "#aaa", marginTop: 2, marginLeft: 4 }}>
              📈 {(() => {
                const parts = [];
                parts.push(
                  <React.Fragment key="word_count">
                    <strong>Word Count:</strong> {song.word_count || song.wordcount || 'n/a'}
                  </React.Fragment>
                );
                
                if (isMysterySong && !isGameWon) {
                  if (discoveredAttributes?.genre?.length > 0) {
                    parts.push(
                      <React.Fragment key="genre">
                        <strong>Genre:</strong> {discoveredAttributes.genre.join(", ")}, ???
                      </React.Fragment>
                    );
                  } else {
                    parts.push(
                      <React.Fragment key="genre">
                        <strong>Genre:</strong> ???
                      </React.Fragment>
                    );
                  }
                } else {
                  if (song.genre && song.genre.length > 0) {
                    const genreDisplay = Array.isArray(song.genre) ? song.genre.join(", ") : song.genre;
                    parts.push(
                      <React.Fragment key="genre">
                        <strong>Genre:</strong> {genreDisplay}
                      </React.Fragment>
                    );
                  } else {
                    parts.push(
                      <React.Fragment key="genre">
                        <strong>Genre:</strong> {'n/a'}
                      </React.Fragment>
                    );
                  }
                }
                
                if (isMysterySong && !isGameWon) {
                  if (discoveredAttributes?.year) {
                    parts.push(
                      <React.Fragment key="year">
                        <strong>Year Released:</strong> {getYearFromDate(song.release_date || song.releasedate) || 'n/a'}
                      </React.Fragment>
                    );
                  } else {
                    parts.push(
                      <React.Fragment key="year">
                        <strong>Year Released:</strong> ???
                      </React.Fragment>
                    );
                  }
                } else {
                  if (song.release_date || song.releasedate) {
                    parts.push(
                      <React.Fragment key="year">
                        <strong>Year Released:</strong> {getYearFromDate(song.release_date || song.releasedate) || 'n/a'}
                      </React.Fragment>
                    );
                  }
                }
                
                if (isMysterySong && !isGameWon) {
                  if (discoveredAttributes?.label) {
                    parts.push(
                      <React.Fragment key="label">
                        <strong>Record Label:</strong> {song.label || 'n/a'}
                      </React.Fragment>
                    );
                  } else {
                    parts.push(
                      <React.Fragment key="label">
                        <strong>Record Label:</strong> ???
                      </React.Fragment>
                    );
                  }
                } else {
                  if (song.label) {
                    parts.push(
                      <React.Fragment key="label">
                        <strong>Record Label:</strong> {song.label || 'n/a'}
                      </React.Fragment>
                    );
                  }
                }
                
                return parts.reduce((prev, curr, index) => 
                  index === 0 ? [curr] : [...prev, ' | ', curr], []
                );
              })()}
            </div>
          </div>
        </div>
        {!isMysterySong && !isGameWon && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(song.id);
            }}
            title="Delete Song"
            style={{
              backgroundColor: QUIT_RED,
              border: "none",
              color: "#fff",
              cursor: "pointer",
              marginRight: 10,
              fontFamily: "'Nunito', sans-serif",
              fontSize: 22,
              fontWeight: "bold",
              borderRadius: 4,
              width: 26,
              height: 26,
              lineHeight: "20px",
              textAlign: "center",
              userSelect: "none",
            }}
          >
            ×
          </button>
        )}
      </div>

      {isExpanded && (
        <div style={{ margin: "8px 8px 0px 18px", paddingLeft: 35, borderLeft: "4px dotted #555" }}>
          <div style={{
            display: "flex",
            fontWeight: "bold",
            fontSize: 18,
            borderBottom: "1px solid #777",
            paddingBottom: 4,
            marginBottom: 4,
            alignItems: "center"
          }}>
            <div style={{ flex: 1, color: "#fff" }}>Section</div>
            <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "flex-end", color: "#fff", alignItems: "center" }}>
              <span>Color&nbsp;&nbsp;</span>
              <MasterColorDropdown
                expandedSectionsColors={expandedSectionsColors}
                color={song.masterColor || ""}
                onChange={(value) => onMasterColorChange(song.id, value)}
                isMysterySong={isMysterySong}
                discoveredSections={discoveredAttributes?.sections || []}
                disabled={!isMysterySong && isGameWon}
              />
            </div>
          </div>
          {sections.map((sectionName, index) => (
            <div
              key={`${sectionName}-${song.sectionColors[sectionName] || ''}`}
              style={{ display: "flex", alignItems: "center", marginBottom: 6 }}
            >
              <span style={{ flex: 1, color: "#ddd", fontSize: 16 }}>
                {isMysterySong && !isGameWon && !discoveredAttributes?.sections.includes(sectionName) 
                  ? "???" 
                  : sectionName}
              </span>
              <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "flex-end" }}>
                <SectionColorDropdown
                  section={sectionName}
                  color={song.sectionColors[sectionName] ?? DEFAULT_SECTION_COLORS[0]}
                  onChange={(section, color) => onSectionColorChange(song.id, section, color)}
                  disabled={(isMysterySong && !isGameWon && !discoveredAttributes?.sections.includes(sectionName)) || (!isMysterySong && isGameWon)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Dashboard component
function Dashboard({
  songs,
  setSongs,
  onSectionColorChange,
  onMasterColorChange,
  onDeleteSong,
  onGetHint,
  collapsed,
  setCollapsed,
  newlyAddedSongId,
  setNewlyAddedSongId,
  discoveredAttributes,
  mysterySongId,
  setMysterySongId,
  firstTime,
  isGameWon,
}) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [expandedSongs, setExpandedSongs] = useState({});

  useEffect(() => {
    if (newlyAddedSongId) {
      setExpandedSongs(prev => ({ ...prev, [newlyAddedSongId]: true }));
      setNewlyAddedSongId(null);
    }
  }, [newlyAddedSongId, setNewlyAddedSongId]);

  useEffect(() => {
    const onResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const wideScreen = windowWidth >= 900 || windowWidth / windowHeight >= 1.5;

  const toggleSongExpansion = (songId) => {
    setExpandedSongs(prev => ({ ...prev, [songId]: !prev[songId] }));
  };

  const dashboardStyle = {
    position: "fixed",
    backgroundColor: "rgba(40, 40, 40, 0.9)",
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 400,
    color: "#ddd",
    boxShadow: "0 0 15px #000a",
    transition: 'all 0.1s cubic-bezier(.4,2,.3,1)',
    overflowY: "auto",
    zIndex: 99,
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    overflow: "visible",
    ...(wideScreen
      ? {
          top: 0,
          right: 0,
          height: "100vh",
          width: collapsed ? 0 : Math.min(400, windowWidth * 0.5),
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          display: "flex",
          flexDirection: "column",
        }
      : {
          left: 0,
          bottom: 0,
          width: "100vw",
          height: collapsed ? 0 : "40vh",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          display: "flex",
          flexDirection: "column",
        }),
  };
  
  const tabStyleBase = {
    position: "fixed",
    cursor: "pointer",
    backgroundColor: "#333",
    color: "#eee",
    fontWeight: "bold",
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    zIndex: 100,
  };
  
  const tabStyle = wideScreen
    ? {
        ...tabStyleBase,
        top: `calc(20px + env(safe-area-inset-top, 0px))`,
        right: collapsed ? 0 : Math.min(400, windowWidth * 0.5),
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
        width: 40,
        height: 60,
      }
    : {
        ...tabStyleBase,
        right: 20,
        bottom: collapsed ? 0 : "40vh",
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        width: 60,
        height: 40,
      };
  
  const tabSymbol = wideScreen ? (collapsed ? "◀" : "▶") : (collapsed ? "▲" : "▼");

  const headerStyle = {
    flexShrink: 0,
    padding: "10px 16px",
    fontWeight: "bold",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #555",
  };

  const songsContainerStyle = {
    flexGrow: 1,
    overflowY: "auto",
    padding: 16,
  };

  return (
    <>
      <div style={tabStyle} onClick={() => setCollapsed(c => !c)} title="Toggle Dashboard">
        {tabSymbol}
      </div>

      <div style={dashboardStyle}>
        {!collapsed && (
          <>
            <div style={headerStyle}>
              <div style={{
                fontSize: 24,
                color: "#fff",
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 'bold',
              }}>
                📟&nbsp;&nbsp;Dashboard
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onGetHint();
                }}
                disabled={firstTime || isGameWon}
                title="Get Hint"
                style={{
                  backgroundColor: firstTime || isGameWon ? "#666" : REG_BLUE,
                  color: firstTime || isGameWon ? "#999" : "#fff",
                  fontFamily: "'Nunito', sans-serif",
                  fontWeight: "bold",
                  fontSize: 14,
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 8px",
                  cursor: firstTime || isGameWon ? "not-allowed" : "pointer",
                  opacity: firstTime || isGameWon ? 0.6 : 1,
                }}
              >
                Get Hint
              </button>
            </div>

            <div style={songsContainerStyle}>
              {songs.length === 0 && (
                <div style={{ color: "#999", fontStyle: "italic" }}>
                  No songs currently visualized.
                </div>
              )}

              {songs.map(song => {
                const expandedSectionsColors = Object.keys(song.sections || {})
                  .filter(section => true)
                  .map(section => song.sectionColors[section])
                  .filter(Boolean);

                return (
                  <SongEntry
                    key={song.id}
                    song={song}
                    isExpanded={!!expandedSongs[song.id]}
                    toggleExpanded={() => toggleSongExpansion(song.id)}
                    onSectionColorChange={onSectionColorChange}
                    onMasterColorChange={onMasterColorChange}
                    onDelete={onDeleteSong}
                    expandedSectionsColors={expandedSectionsColors}
                    discoveredAttributes={song.id === mysterySongId ? discoveredAttributes : null}
                    mysterySongId={mysterySongId}
                    isGameWon={isGameWon}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  )
}

// Main song search
function SongSearch({
  selectedSongId,
  setSelectedSongId,
  onSelect,
  query,
  setQuery,
  selectedSongText,
  setSelectedSongText,
  isGameWon
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { width, height, dwidth } = useWindowSize();
  
  const debounceTimerRef = useRef(null);

  async function performSearch(searchQuery) {
    if (!searchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    
    setLoading(true);
    setHasSearched(true);
    try {
      const resp = await fetch(`/.netlify/functions/songSearch?q=${encodeURIComponent(searchQuery)}`);
      const data = await resp.json();
      
      const hits = data.response?.hits ?? [];
      
      const filteredHits = hits.filter(({ result }) => {
        const artistName = result.primary_artist?.name || "";
        return !(
          artistName.startsWith("Genius ") ||
          artistName.endsWith(" Genius") ||
          artistName.includes(" Genius ")
        );
      });
      
      setResults(filteredHits);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!query) return;

    setHasSearched(false);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    await performSearch(query);
    setShowDropdown(true);
    setHasSearched(true);
  }

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedSongText("");
    
    if (newQuery.trim()) {
      setResults([]);
      setShowDropdown(true);
      setHasSearched(false);
    } else {
      setShowDropdown(false);
      setResults([]);
      setHasSearched(false);
    }
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    if (newQuery.trim()) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(newQuery);
      }, 500);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    function handleClick(e) {
      if (!e.target.closest("#song-search-inline-dropdown")) setShowDropdown(false);
    }
    if (showDropdown) {
      window.addEventListener("mousedown", handleClick);
      return () => window.removeEventListener("mousedown", handleClick);
    }
  }, [showDropdown]);

  return (
    <div
      id="song-search-inline"
      style={{
        position: "relative",
        width: dwidth < 450 
          ? Math.max(dwidth - 20 - 2 - 12 - 3 - 1 - 1 - 10 - 8 - 1 - 60 - 1 - 12 - 2 - 20, 150)
          : (width < 900 ? 210 : 250),
        minHeight: 30,
        fontFamily: "'Sniglet', sans-serif",
        fontWeight: 400,
        borderRadius: 12,
        background: "black",
        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
        opacity: isGameWon ? 0.5 : 1,
        pointerEvents: isGameWon ? 'none' : 'auto',
      }}
    >
      <form
        onSubmit={handleSearch}
        style={{
          display: "flex",
          width: "100%",
          height: "100%",
          border: "1px solid #348FD9",
          borderRadius: 12,
          alignItems: "center",
        }}
      >
        <input
          id="song-search-input"
          type="text"
          placeholder="Guess a song"
          value={selectedSongText || query}
          spellCheck={false}
          onChange={handleInputChange}
          onClick={() => {
            if (selectedSongText) {
              setSelectedSongId(null);
              setSelectedSongText("");
              setQuery("");
            }
          }}
          disabled={isGameWon}
          style={{
            flex: 1,
            padding: "0 2px 0 12px",
            border: "none",
            background: "transparent",
            color: "white",
            fontSize: 18,
            height: 30,
            outline: "none",
            minWidth: 75,
          }}
        />
        <button
          type="submit"
          title="Search Song"
          disabled={isGameWon}
          style={{
            border: "none",
            background: REG_BLUE,
            color: "#fff",
            fontWeight: 700,
            fontSize: 22,
            height: 30,
            width: 40,
            cursor: isGameWon ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "0 10px 10px 0",
            transition: "background 0.15s",
            userSelect: "none",
          }}
          onMouseEnter={e => !isGameWon && (e.currentTarget.style.background = "#2071B2")}
          onMouseLeave={e => e.currentTarget.style.background = REG_BLUE}
        >
          🔍
        </button>
      </form>
      {loading && (
        <div
          style={{
            padding: "8px 0 2px 10px",
            color: REG_BLUE,
            fontSize: 14,
            background: "transparent",
            textAlign: "left",
            userSelect: "none",
          }}
        >
          Loading…
        </div>
      )}
      {showDropdown && results.length > 0 && (
        <div
          id="song-search-inline-dropdown"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            background: DEEP_BLUE,
            opacity: 0.99,
            borderRadius: 10,
            boxShadow: "0 4px 24px rgba(0,0,0,0.22)",
            zIndex: 20,
            marginTop: 4,
            maxHeight: height * 0.35,
            overflowY: "auto",
          }}
        >
          {results.map(({ result }) => (
            <div
              key={result.id}
              onClick={() => {
                const text = `${result.title} - ${result.primary_artist?.name || ""}`;
                setSelectedSongText(text);
                setSelectedSongId(result.id);
                setShowDropdown(false);
                if (onSelect) onSelect(result);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 16px",
                cursor: "pointer",
                color: "white",
                borderBottom: "1.5px solid #313c4a",
                transition: "background 0.13s",
                userSelect: "none",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#313c4a"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <img
                src={result.song_art_image_thumbnail_url}
                alt="art"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 6,
                  objectFit: "cover",
                  marginRight: 14,
                }}
              />
              <div>
                <div style={{ fontSize: 18, fontWeight: 400 }}>
                  {result.title}
                </div>
                <div style={{ fontSize: 14, color: BABY_BLUE, fontWeight: 400 }}>
                  {result.primary_artist?.name || ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showDropdown && results.length === 0 && !loading && hasSearched && query.trim() && (
        <div
          style={{
            padding: "8px 0 2px 10px",
            color: REG_BLUE,
            fontSize: 14,
            background: "transparent",
            textAlign: "left",
            userSelect: "none",
          }}
        >
          No results found!
        </div>
      )}
    </div>
  );
}

// PulseText for loading
function PulseText({ children, speed = 1 }) {
  const [time, setTime] = useState(0);

  useEffect(() => {
    let frameId;
    const animate = () => {
      setTime(prev => prev + 0.02 * speed);
      frameId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(frameId);
  }, [speed]);

  const opacity = 0.625 + 0.375 * Math.sin(time * Math.PI);

  return (
    <div style={{ opacity }}>
      {children}
    </div>
  );
}

// Helper function: getting text color info for gradient purposes
const getTextColorInfo = (colors) => {
  if (!colors || colors.length === 0) return { color: "#fff", isMultiColor: false };
  
  const validColors = (Array.isArray(colors) ? colors : [colors]).filter(c => c && c !== 'transparent');
  
  if (validColors.length === 0) return { color: "#fff", isMultiColor: false };
  if (validColors.length === 1) return { color: validColors[0], isMultiColor: false };
  
  return {
    color: validColors[0],
    isMultiColor: true,
    validColors: validColors,
    gradientStyle: {
      backgroundImage: `linear-gradient(90deg, ${validColors.join(", ")})`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      display: "inline-block",
      color: validColors[0]
    }
  };
};

// Double-tap on mobile
function useDoubleTap(callback, ms = 350, canvasId = 'orbit-canvas') {
  useEffect(() => {
    let lastTap = 0;
    let lastTouchEndTime = 0;
    let touchMoved = false;

    const handleTouchStart = (e) => {
      touchMoved = false;
      if (e.touches.length > 1) touchMoved = true;
      e.target.dataset.startX = e.touches[0].clientX;
      e.target.dataset.startY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const sx = parseInt(e.target.dataset.startX, 10);
      const sy = parseInt(e.target.dataset.startY, 10);
      const dx = Math.abs(e.touches[0].clientX - sx);
      const dy = Math.abs(e.touches[0].clientY - sy);
      if (dx > 10 || dy > 10) touchMoved = true;
    };

    const handleTouchEnd = (e) => {
      if (touchMoved || e.changedTouches.length !== 1) return;

      const now = Date.now();
      if (now - lastTap < ms && now - lastTouchEndTime > 40) {
        callback(e);
        lastTap = 0;
      } else {
        lastTap = now;
      }
      lastTouchEndTime = now;
    };

    const el = document.getElementById(canvasId);

    if (!el) {
      console.log("Element not found! Delaying or refactoring may be needed.");
      return;
    }
    el.addEventListener('touchstart', handleTouchStart);
    el.addEventListener('touchmove', handleTouchMove);
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
      if (!el) return;
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [callback, ms, canvasId]);
}

// Hint Selection Modal
function HintSelectionModal({ onClose, onSelectHint, assistScore }) {
  const { width, height } = useWindowSize();

  const hints = [
    { type: 'word', label: 'Reveal Random Word', cost: 1, emoji: '💬' },
    { type: 'genre', label: 'Reveal Random Genre', cost: 5, emoji: '🎭' },
    { type: 'year', label: 'Reveal Year Released', cost: 25, emoji: '🗓️' },
    { type: 'artist', label: 'Reveal Artist Name', cost: 100, emoji: '👤' },
  ];

  return createPortal(
    <div
      className="hint-modal-overlay"
      onClick={(e) => {
        if (e.target.classList.contains('hint-modal-overlay')) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: width,
        height: height,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 500,
      }}
    >
      <div
        style={{
          backgroundColor: DEEP_BLUE,
          opacity: 0.95,
          borderRadius: '12px',
          padding: '24px',
          minWidth: width < 600 ? width * 0.8 : '400px',
          maxWidth: width * 0.9,
          width: 'auto',
          maxHeight: 'calc(90vh - 100px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              fontFamily: "'Sniglet', sans-serif",
              fontSize: '24px',
              color: 'white',
              margin: 0,
            }}
          >
            Select a Hint
          </h2>
          <span
            onClick={onClose}
            style={{
              fontSize: '28px',
              cursor: 'pointer',
              color: '#a9a9a9',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => (e.target.style.color = 'white')}
            onMouseLeave={(e) => (e.target.style.color = '#a9a9a9')}
          >
            ×
          </span>
        </div>

        <div
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '14px',
            color: '#ccc',
            marginBottom: '16px',
          }}
        >
          Hint Points used so far: <strong style={{ color: REG_BLUE }}>{assistScore}</strong>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {hints.map((hint) => (
            <button
              key={hint.type}
              onClick={() => onSelectHint(hint.type, hint.cost)}
              style={{
                fontFamily: "'Sniglet', sans-serif",
                fontSize: '16px',
                padding: '12px 18px',
                backgroundColor: 'rgba(0,0,0, 0.8)',
                color: 'white',
                border: '1.5px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#444')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0, 0.8)')}
            >
              <span>
                {hint.emoji} &nbsp;&nbsp; {hint.label}
              </span>
              <span style={{ fontSize: '14px', opacity: 0.8 }}>+{hint.cost}</span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Win Screen Modal
function WinScreenModal({ 
  onClose, 
  guessCount, 
  assistScore, 
  mysterySong,
  wordDiscoveryBreakdown,
  onPlayAgain 
}) {
  const { width, height } = useWindowSize();

  const generateShareText = () => {
    let text = `🎵 LyricaL - Daily Song\n\n`;
    text += `✅ Guessed in ${guessCount} ${guessCount === 1 ? 'try' : 'tries'}\n`;
    if (assistScore > 0) {
      text += `🆘 Hint Points Used: ${assistScore}\n`;
    }
    text += `\nWord Count Progress:\n`;
    wordDiscoveryBreakdown.forEach(item => {
      text += `${item.emoji} ${item.percentage}% - ${item.guesses} ${item.guesses === 1 ? 'guess' : 'guesses'}\n`;
    });
    text += `\nhttps://reilly.fun/lyrical`;
    return text;
  };

  const handleShare = () => {
    const shareText = generateShareText();
    
    if (navigator.share) {
      navigator.share({
        text: shareText,
      }).catch((error) => {
        if (error.name !== 'AbortError') {
          console.log('Share failed:', error);
        }
      });
    } else {
      navigator.clipboard.writeText(shareText).then(() => {
        alert('Results copied to clipboard!');
      });
    }
  };

  return createPortal(
    <div
      className="win-screen-overlay"
      onClick={(e) => {
        if (e.target.classList.contains('win-screen-overlay')) {
          onClose();
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: width,
        height: height,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 500,
      }}
    >
      <div
        style={{
          backgroundColor: DEEP_BLUE,
          opacity: 0.95,
          borderRadius: '12px',
          padding: '32px',
          minWidth: width < 600 ? width * 0.8 : '400px',
          maxWidth: width * 0.9,
          width: 'auto',
          maxHeight: 'calc(90vh - 100px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          position: 'relative',
          overflowY: 'auto',
        }}
      >
        <span
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#a9a9a9',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => (e.target.style.color = 'white')}
          onMouseLeave={(e) => (e.target.style.color = '#a9a9a9')}
        >
          ×
        </span>

        <h2
          style={{
            fontFamily: "'Sniglet', sans-serif",
            fontSize: '28px',
            color: NEON_GREEN,
            margin: '0 0 8px 0',
            textAlign: 'center',
          }}
        >
          Congratulations!
        </h2>

        <p
          style={{
            fontFamily: "'Nunito', sans-serif",
            fontSize: '18px',
            color: 'white',
            textAlign: 'center',
            margin: '8px 0 24px 0',
          }}
        >
          You found the mystery song!
        </p>

        <div
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '16px',
              color: 'white',
              marginBottom: '12px',
              textAlign: 'center',
            }}
          >
            <strong>{mysterySong?.name || "Mystery Song"}</strong>
            <br />
            <span style={{ color: '#ccc', fontSize: '14px' }}>
              by {mysterySong?.artist || "Unknown Artist"}
            </span>
          </div>

          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '16px',
              color: 'white',
              textAlign: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ marginBottom: '8px' }}>
              ✅ Guessed in <strong style={{ color: NEON_GREEN }}>{guessCount}</strong>{' '}
              {guessCount === 1 ? 'try' : 'tries'}
            </div>
            {assistScore > 0 && (
              <div style={{ fontSize: '14px', color: '#ccc' }}>
                🆘 Hint Points Used: <strong>{assistScore}</strong>
              </div>
            )}
          </div>

          <div
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: '14px',
              color: 'white',
            }}
          >
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                fontSize: '15px',
              }}
            >
              Word Count Progress:
            </div>
            {wordDiscoveryBreakdown.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom: index < wordDiscoveryBreakdown.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <span>
                  {item.emoji} {item.percentage}%
                </span>
                <span style={{ color: '#ccc' }}>
                  {item.guesses} {item.guesses === 1 ? 'guess' : 'guesses'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p
          style={{
            fontFamily: "'Sniglet', sans-serif",
            fontSize: '16px',
            color: 'white',
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          If you enjoyed, feel free to:
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <button
            onClick={handleShare}
            style={{
              fontFamily: "'Sniglet', sans-serif",
              fontSize: '16px',
              padding: '12px',
              backgroundColor: 'rgba(0,0,0, 0.8)',
              color: 'white',
              border: '1.5px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 400,
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#444')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0, 0.8)')}
          >
            🔗 Share
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onPlayAgain}
              style={{
                flex: 1,
                fontFamily: "'Sniglet', sans-serif",
                fontSize: '16px',
                padding: '12px',
                backgroundColor: NEON_GREEN,
                color: 'black',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 400,
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              🔄 Play Again
            </button>

            <button
              onClick={() => window.open('https://reilly.fun/lyrics', '_blank')}
              style={{
                flex: 1,
                fontFamily: "'Sniglet', sans-serif",
                fontSize: '16px',
                padding: '12px',
                backgroundColor: '#c493ff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 400,
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              🔭 Explore More
            </button>
          </div>

          {/* <button
            onClick={() => window.open('https://buymeacoffee.com/reillyfun', '_blank')}
            style={{
              fontFamily: "'Sniglet', sans-serif",
              fontSize: '14px',
              padding: '10px',
              backgroundColor: 'rgba(0,0,0, 0.8)',
              color: 'white',
              border: '1.5px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 400,
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#444')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0, 0.8)')}
          >
            ☕ Buy Me a Coffee
          </button> */}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Main visualization component
function MusicViz({ 
  dashboardSongs,
  setDashboardSongs,
  selectedSongId,
  setSelectedSongId,
  searchQuery,
  setSearchQuery,
  selectedSongText,
  setSelectedSongText,
  newlyAddedSongId,
  setNewlyAddedSongId,
  isDashboardCollapsed,
  setIsDashboardCollapsed,
  discoveredAttributes,
  setDiscoveredAttributes,
  mysterySongId,
  setMysterySongId,
  guessCount,
  setGuessCount,
  isGameWon,
  setIsGameWon,
  endMsg,
  setEndMsg,
  firstTime,
  setFirstTime,
  selectedWordInfo,
  setSelectedWordInfo,
  assistScore,
  setAssistScore,
  wordDiscoveryHistory,
  setWordDiscoveryHistory,
  showWinScreen,
  setShowWinScreen,
  showHintModal,
  setShowHintModal
}) {
  const navigate = useNavigate();
  
  const [infoBoxPosition, setInfoBoxPosition] = useState({ x: 0, y: 0 });
  const [infoBoxDimensions, setInfoBoxDimensions] = useState({ width: 0, height: 0 });
  const infoBoxRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [isTitleCollapsed, setIsTitleCollapsed] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Controls');

  const { width, height, dwidth, dheight } = useWindowSize();
  const isMobile = window.matchMedia('(pointer: coarse)').matches;

  const titleRef = useRef();
  const menuButtonRef = useRef();
  const [centerY, setCenterY] = useState(null);
  const [resetCameraTrigger, setResetCameraTrigger] = useState(0);
  const controlsRef = useRef();
  const groupRef = useRef();

  const guessedSong = dashboardSongs.find(song => song.id !== mysterySongId);
  const mysterySong = dashboardSongs.find(song => song.id === mysterySongId);
  const mysteryWords = mysterySong ? mysterySong.words : [];

  const allWords = dashboardSongs.flatMap(song => song.words || []);

  const wordMap = new Map();
  allWords.forEach(w => {
    const pos = w.vector || w.position;
    if (!pos) return;
    
    const key = `${w.word.toLowerCase()}_${pos[0].toFixed(2)}_${pos[1].toFixed(2)}_${pos[2].toFixed(2)}`;
    
    if (!wordMap.has(key)) {
      wordMap.set(key, {
        word: w.word,
        position: pos,
        colors: [w.color],
        section: w.section,
        radius: w.radius,
        wikiFreq: w.wikiFreq,
        hasLabel: w.hasLabel
      });
    } else {
      const existing = wordMap.get(key);
      if (!existing.colors.includes(w.color)) {
        existing.colors.push(w.color);
      }
      if (w.hasLabel) {
        existing.hasLabel = true;
      }
    }
  });

  const uniqueWords = Array.from(wordMap.values());
  const aggregatedWords = uniqueWords.map(w => w.word);
  const aggregatedPositions = uniqueWords.map(w => w.position);
  const aggregatedColors = uniqueWords.map(w => w.colors);
  const aggregatedSections = uniqueWords.map(w => w.section);
  const aggregatedRadii = uniqueWords.map(w => w.radius);
  const aggregatedWikiFreqs = uniqueWords.map(w => w.wikiFreq);
  const aggregatedHasLabels = uniqueWords.map(w => w.hasLabel);

  const updateDiscoveredAttributes = useCallback((guessedSong) => {

    setDiscoveredAttributes(prev => {
      if (!mysterySong || !guessedSong) return prev;
      const discovered = {
        name: prev.name,
        sections: [...prev.sections],
        artist: prev.artist,
        genre: [...prev.genre],
        label: prev.label,
        year: prev.year
      };
      
      const mysterySections = Object.keys(mysterySong.sections || {});
      const guessedSections = Object.keys(guessedSong.sections);

      const newSections = mysterySections.filter(ms => {
        const normalizedMystery = ms.toLowerCase().trim();
        const hasMatchingGuessed = guessedSections.some(gs => {
          const normalizedGuessed = gs.toLowerCase().trim();
          return normalizedMystery === normalizedGuessed;
        });
        const alreadyDiscovered = discovered.sections.some(ds => 
          ds.toLowerCase().trim() === normalizedMystery
        );
        
        return hasMatchingGuessed && !alreadyDiscovered;
      });

      discovered.sections = [...discovered.sections, ...newSections];
      
      if (!discovered.name && mysterySong.name && guessedSong.name) {
        discovered.name = mysterySong.name.toLowerCase() === guessedSong.name.toLowerCase();
      }

      if (!discovered.artist && mysterySong.artist && guessedSong.artist) {
        discovered.artist = mysterySong.artist.toLowerCase() === guessedSong.artist.toLowerCase();
      }
      
      if (mysterySong.genre && guessedSong.genre) {
        const filteredMysteryGenres = excludeBadGenres(mysterySong.genre);
        const filteredGuessedGenres = excludeBadGenres(guessedSong.genre);
        
        const newGenres = filteredMysteryGenres.filter(mg => 
          filteredGuessedGenres.some(gg => mg.toLowerCase() === gg.toLowerCase()) && 
          !discovered.genre.includes(mg)
        );
        discovered.genre = [...discovered.genre, ...newGenres];
      }
      
      if (!discovered.label && mysterySong.label && guessedSong.label) {
        discovered.label = mysterySong.label.toLowerCase() === guessedSong.label.toLowerCase();
      }
      
      if (!discovered.year) {
        const mysteryYear = mysterySong.release_date ? new Date(mysterySong.release_date).getFullYear() : null;
        const guessedYear = guessedSong.release_date ? new Date(guessedSong.release_date).getFullYear() : null;
        if (mysteryYear && guessedYear) {
          discovered.year = mysteryYear === guessedYear;
        }
      }
      
      return discovered;
    });
  }, [mysterySong, setDiscoveredAttributes]);

  useEffect(() => {
    async function loadMystery() {
      try {
        const response = await fetch("https://witty-moccasin-fully.ngrok-free.app/api/song-of-the-day", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true"
          }
        });
        const mysteryData = await response.json();

        const SPREAD = 15;
        let mysteryWordList = [];
        Object.entries(mysteryData.vectors).forEach(([section, words]) => {
          Object.entries(words).forEach(([word, wordData]) => {
            mysteryWordList.push({
              word,
              section,
              vector: wordData.vector.map(coord => coord * SPREAD),
              sectionFreq: wordData.section_freq,
              songFreq: wordData.song_freq,
              wikiFreq: wordData.wiki_freq,
              radius: wordData.radius,
              color: DEFAULT_SECTION_COLORS[1],
              hasLabel: false
            });
          });
        });

        const mysterySections = {};
        Object.keys(mysteryData.vectors).forEach(section => {
          mysterySections[section] = true;
        });

        const mysterySongEntry = {
          dashboardId: "mystery-song",
          id: mysteryData.geniusSongId || 'n/a',
          geniusSongId: mysteryData.geniusSongId || 'n/a',
          name: mysteryData.name || 'n/a',
          artist: mysteryData.artist || 'n/a',
          language: mysteryData.language || 'n/a',
          release_date: mysteryData.release_date || 'n/a',
          sections: mysterySections,
          sectionColors: Object.keys(mysterySections).reduce((acc, section) => {
            acc[section] = DEFAULT_SECTION_COLORS[1];
            return acc;
          }, {}),
          words: mysteryWordList,
          wordcount: mysteryWordList.length,
          masterColor: DEFAULT_SECTION_COLORS[1],
          genre: excludeBadGenres(mysteryData.genre) || [],
          label: mysteryData.label || 'n/a',
          isMystery: true
        };

        setDashboardSongs([mysterySongEntry]);
        setNewlyAddedSongId(mysterySongEntry.geniusSongId);
        setMysterySongId(mysterySongEntry.geniusSongId);
      } catch (error) {
        console.error("Failed to load mystery song", error);
      }
    }
    loadMystery();
  }, []); // eslint-disable-line

  const worldToScreen = useCallback((worldPosition,camera, renderer) => {
    const vector = new THREE.Vector3(...worldPosition);
    vector.project(camera);
    
    let x = (vector.x * 0.5 + 0.5) * renderer.domElement.clientWidth;
    let y = (vector.y * -0.5 + 0.5) * renderer.domElement.clientHeight;
    
    const boxWidth = infoBoxDimensions.width || 200;
    const boxHeight = infoBoxDimensions.height || 50;
    const margin = 10;
    const topOffset = width < 900 ? 15 : 25;

    const boxLeft = x - boxWidth/2;
    const boxRight = x + boxWidth/2;
    const boxTop = y - boxHeight - topOffset;
    const boxBottom = y - topOffset;
    
    const minX = margin;
    const maxX = dwidth - margin;
    const minY = margin;
    const maxY = dheight - margin;
    
    if (boxLeft < minX) {
      x = minX + boxWidth/2;
    } else if (boxRight > maxX) {
      x = maxX - boxWidth/2;
    }
    
    if (boxTop < minY) {
      y = minY + boxHeight + topOffset;
    } else if (boxBottom > maxY) {
      y = maxY + topOffset;
    }
    
    return { x, y };
  }, [infoBoxDimensions, dwidth, dheight, width])

  async function handleAddSong() {
    setLoading(true);
    setLoadingMsg("Loading your guess...");

    if (!selectedSongId) {
      setEndMsg("Please search and select a song from the dropdown.");
      setLoading(false);
      return;
    }

    try {
      setLoadingMsg("Loading lyrics...");
      const lyricsResp = await fetch("https://witty-moccasin-fully.ngrok-free.app/api/get-lyrics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ songId: selectedSongId })
      });
      const lyricsData = await lyricsResp.json();

      if (lyricsData.status?.length) {
        const lastStatus = lyricsData.status[lyricsData.status.length - 1];
        setLoadingMsg(lastStatus);
        if ('error' in lyricsData || !lyricsData.vectors || Object.keys(lyricsData.vectors).length === 0) {
          setEndMsg(lastStatus + " Unknown error.");
          setLoading(false);
          return;
        }
      } else {
        setEndMsg("");
      }

      const sectionVectors = lyricsData.vectors;
      if (!sectionVectors || Object.keys(sectionVectors).length === 0) {
        setLoading(false);
        return;
      }

      const genre = excludeBadGenres(lyricsData.genre);
      const label = lyricsData.label || null;
      setLoadingMsg("Processing your guess...");
      let wordVectorList = [];
      let sectionNames = Object.keys(sectionVectors);

      sectionNames.forEach(section => {
        Object.entries(sectionVectors[section]).forEach(([word, wordData]) => {
          wordVectorList.push({
            word,
            section,
            vector: wordData.vector,
            sectionFreq: wordData.section_freq,
            songFreq: wordData.song_freq,
            wikiFreq: wordData.wiki_freq,
            radius: wordData.radius
          });
        });
      });
      const SPREAD = 15;
      const positions = wordVectorList.map(wv => wv.vector.map(coord => coord * SPREAD));
      const visualizedWords = wordVectorList.map((wv, idx) => ({
        word: wv.word,
        section: wv.section,
        color: DEFAULT_SECTION_COLORS[0],
        position: positions[idx],
        sectionFreq: wv.sectionFreq,
        songFreq: wv.songFreq,
        wikiFreq: wv.wikiFreq,
        radius: wv.radius,
        hasLabel: true
      }));

      setLoadingMsg("Loading song metadata...");
      const metaResp = await fetch(`.netlify/functions/getMeta?id=${selectedSongId}`);
      const metaJson = await metaResp.json();

      const sections = {};
      sectionNames.forEach(s => { sections[s] = true; });

      const guessedSongData = {
        id: selectedSongId,
        name: metaJson.response.song.title_with_featured || metaJson.response.song.full_title || metaJson.response.song.title,
        artist: metaJson.response.song.primary_artist.name || metaJson.response.song.artist_names || "Unknown Artist",
        language: metaJson.response.song.language || "n/a",
        release_date: metaJson.response.song.release_date || "n/a",
        sections,
        words: visualizedWords,
        wordcount: wordVectorList.length,
        genre: genre,
        label: label,
        sectionColors: sectionNames.reduce((acc, section) => {
          acc[section] = DEFAULT_SECTION_COLORS[0];
          return acc;
        }, {}),
        masterColor: DEFAULT_SECTION_COLORS[0]
      };

      setGuessCount(prev => prev + 1);

      const isVictory = mysterySong && selectedSongId === mysterySong.geniusSongId;

      if (isVictory) {
        if (firstTime) setIsDashboardCollapsed(false);
        setFirstTime(false);
        
        updateDiscoveredAttributes(guessedSongData);

        setDashboardSongs(current =>
          current.map(song => {
            if (song.id === mysterySong.geniusSongId) {
              return {
                ...song,
                name: mysterySong.name,
                artist: mysterySong.artist,
                genre: excludeBadGenres(mysterySong.genre),
                label: mysterySong.label,
                sectionColors: Object.keys(song.sections).reduce((acc, section) => {
                  acc[section] = NEON_GREEN;
                  return acc;
                }, {}),
                masterColor: NEON_GREEN,
                words: song.words.map(w => ({ ...w, color: NEON_GREEN, hasLabel: true }))
              };
            }
            return song;
          }).filter(song => song.id === mysterySongId)
        );

        setIsGameWon(true);
        setShowWinScreen(true);
        setEndMsg('');
      } else {
        updateDiscoveredAttributes(guessedSongData);

        const guessedWords = guessedSongData.words || [];
        const currentMysterySong = dashboardSongs.find(song => song.id === mysterySongId);
        const mysteryWords = currentMysterySong ? currentMysterySong.words : [];

        const matchedCount = mysteryWords.filter(mystery =>
          guessedWords.some(guessed =>
            guessed.word.toLowerCase() === mystery.word.toLowerCase()
          )
        ).length;

        const updatedMysteryWords = mysteryWords.map(w =>
          guessedWords.some(gw => gw.word.toLowerCase() === w.word.toLowerCase())
            ? { ...w, hasLabel: true }
            : w
        );

        const numUniqueFound = updatedMysteryWords.filter(w => w.hasLabel).length;
        const totalWords = updatedMysteryWords.length;

        setWordDiscoveryHistory(prev => [...prev, { guessNumber: guessCount + 1, wordsFound: numUniqueFound, totalWords }]);

        setDashboardSongs(current => {
          const mysterySong = current.find(song => song.id === mysterySongId);
          const newGuess = {
            ...guessedSongData,
            dashboardId: 'guessed-song'
          };
          const next = mysterySong ? [mysterySong, newGuess] : [newGuess];

          return next.map(song =>
            song.id !== mysterySongId
              ? song
              : {
                  ...song,
                  words: song.words.map(w =>
                    newGuess.words.some(gw => gw.word.toLowerCase() === w.word.toLowerCase())
                      ? { ...w, hasLabel: true }
                      : w
                  )
                }
          );
        });

        setNewlyAddedSongId(selectedSongId);
        
        if (firstTime) setIsDashboardCollapsed(false);
        
        setFirstTime(false);

        setEndMsg(
          <>
            <span style={{ fontSize: "18px" }}>
              ➙ Guess #{guessCount + 1}:
            </span>
            <br />
            {`Words matched: ${matchedCount}`}
            {"  |  "}
            {`Total words found: ${numUniqueFound}/${totalWords}`}
          </>
        );
      }

      setLoadingMsg("");
      setLoading(false);
    } catch (err) {
      console.error("Error caught:", err);
      const errorMsg = String(err);
      let displayMsg = "Loading error. Please try another song.";
      if (errorMsg.includes("TypeError: Failed to fetch")) {
        displayMsg = "Server busy. Please try again!";
      }
      setLoadingMsg(displayMsg);
      setEndMsg(displayMsg);
      setLoading(false);
    }
  }

  function handleReset() {
    setAutoRotate(true);
    setResetCameraTrigger(prev => prev + 1);
  }

  const handleResetComplete = useCallback(() => {
  }, []);

  useEffect(() => {
    if (infoBoxRef.current && selectedWordInfo) {
      requestAnimationFrame(() => {
        if (infoBoxRef.current) {
          const rect = infoBoxRef.current.getBoundingClientRect();
          setInfoBoxDimensions({
            width: rect.width,
            height: rect.height
          });
        }
      });
    }
  }, [selectedWordInfo]);

  const songIds = dashboardSongs.map(s => s.id).sort().join(',');
  useEffect(() => {
    setSelectedWordInfo(null);
  }, [songIds, setSelectedWordInfo]);

  useEffect(() => {
    function updateCenterY() {
      if (titleRef.current && menuButtonRef.current) {
        const titleRect = titleRef.current.getBoundingClientRect();
        const menuRect = menuButtonRef.current.getBoundingClientRect();
        const mid = (titleRect.bottom + menuRect.top) / 2;
        setCenterY(mid > 50 ? mid : -1);
      }
    }

    const timer = setTimeout(() => {
      updateCenterY();
    }, 100);

    const handleResize = () => {
      clearTimeout(timer);
      updateCenterY();
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [isTitleCollapsed, isDashboardCollapsed, width, height]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleMenuOutsideClick = (e) => {
    if (e.target.classList.contains('menu-overlay')) {
      setIsMenuOpen(false);
    }
  };

  const handleWordClick = (event, wordInfo) => {

    setSelectedWordInfo(null);
    
    const songOccurrences = [];
    
    const appearsInMystery = mysterySong && mysteryWords && 
      mysteryWords.some(w => w.word.toLowerCase() === wordInfo.word.toLowerCase());
    const appearsInGuessed = guessedSong && 
      guessedSong.words.some(w => w.word.toLowerCase() === wordInfo.word.toLowerCase());
    
    const headerColors = [];
    
    if (appearsInMystery && mysteryWords) {
      const mysteryWordMatches = mysteryWords.filter(w => 
        w.word.toLowerCase() === wordInfo.word.toLowerCase()
      );
      
      if (mysteryWordMatches.length > 0) {
        const mysterySectionsData = [];
        const mysterySongEntry = dashboardSongs.find(song => song.id === mysterySong.geniusSongId);
        
        mysteryWordMatches.forEach(w => {
          const sectionColor = mysterySongEntry?.sectionColors[w.section] || DEFAULT_SECTION_COLORS[1];
          
          if (!headerColors.includes(sectionColor)) {
            headerColors.push(sectionColor);
          }
          
          if (!mysterySectionsData.find(s => s.name === w.section)) {
            mysterySectionsData.push({
              name: isGameWon || discoveredAttributes.sections.includes(w.section) ? w.section : "???",
              color: sectionColor
            });
          }
        });
        
        songOccurrences.push({
          songName: isGameWon || discoveredAttributes.name ? mysterySong.name : "???",
          sections: mysterySectionsData
        });
      }
    }
    
    if (appearsInGuessed && guessedSong) {
      const sectionsData = [];
      const sectionOrder = Object.keys(guessedSong.sections || {});
      const guessedSongEntry = dashboardSongs.find(song => song.id === guessedSong.id);
      guessedSong.words
        .filter(w => w.word.toLowerCase() === wordInfo.word.toLowerCase())
        .forEach(w => {
          const sectionColor = guessedSongEntry?.sectionColors[w.section] || DEFAULT_SECTION_COLORS[0];
          if (!headerColors.includes(sectionColor)) {
            headerColors.push(sectionColor);
          }
          
          const existingSection = sectionsData.find(s => s.name === w.section);
          if (!existingSection) {
            sectionsData.push({
              name: w.section,
              color: sectionColor
            });
          }
        });
      
      if (sectionsData.length > 0) {
        sectionsData.sort((a, b) => {
          const indexA = sectionOrder.indexOf(a.name);
          const indexB = sectionOrder.indexOf(b.name);
          return indexA - indexB;
        });
        
        songOccurrences.push({
          songName: guessedSong.name,
          sections: sectionsData
        });
      }
    }
    
    setTimeout(() => {
      setSelectedWordInfo({
        ...wordInfo,
        songOccurrences,
        isSharedWord: appearsInMystery && appearsInGuessed,
        colors: headerColors.length > 0 ? headerColors : [wordInfo.color || '#fff']
      });
    }, 0);
  };

  const handleCanvasClick = (event) => {
    if (event.object) {
      return;
    }
    setSelectedWordInfo(null);
  };

  useDoubleTap(handleReset, 350, 'orbit-canvas');

  function handleDragStart() {
    setAutoRotate(false);
  }

  function ScreenPositionTracker({ selectedWordInfo, onPositionUpdate }) {
    const { camera, gl } = useThree();
    
    useFrame(() => {
      if (selectedWordInfo && selectedWordInfo.position) {
        const screenPos = worldToScreen(selectedWordInfo.position, camera, gl);
        onPositionUpdate(screenPos);
      }
    });
    
    return null;
  }

  const handleSelectHint = useCallback((hintType, cost) => {
    setShowHintModal(false);
    setAssistScore(prev => prev + cost);

    if (hintType === 'word') {
      const currentMysterySong = dashboardSongs.find(song => song.id === mysterySongId);
      if (!currentMysterySong) return;
      
      const undiscovered = currentMysterySong.words.filter(w => !w.hasLabel);
      if (!undiscovered.length) return;
      
      const idx = Math.floor(Math.random() * undiscovered.length);
      const wordToReveal = undiscovered[idx];
      
      // Update dashboard songs to reveal the word
      setDashboardSongs(current =>
        current.map(song =>
          song.id !== mysterySongId
            ? song
            : {
                ...song,
                words: song.words.map(w =>
                  !w.hasLabel && w.word === wordToReveal.word
                    ? { ...w, hasLabel: true }
                    : w
                )
              }
        )
      );
      
      // Set up word info for display - simulate a click event
      setTimeout(() => {
        const mysterySongEntry = dashboardSongs.find(song => song.id === mysterySongId);
        const sectionColor = mysterySongEntry?.sectionColors[wordToReveal.section] || DEFAULT_SECTION_COLORS[1];
        
        const songOccurrences = [{
          songName: discoveredAttributes.name ? mysterySongEntry.name : "???",
          sections: [{
            name: discoveredAttributes.sections.includes(wordToReveal.section) ? wordToReveal.section : "???",
            color: sectionColor
          }]
        }];
        
        setSelectedWordInfo({
          word: wordToReveal.word,
          wikiFreq: wordToReveal.wikiFreq,
          position: wordToReveal.vector,
          colors: [sectionColor],
          songOccurrences,
          isSharedWord: false
        });
      }, 100);
      
      // Add message to title box
      setEndMsg(`Revealed word: ${wordToReveal.word}`);
    } else if (hintType === 'genre') {
      if (!mysterySong || !mysterySong.genre) return;
      
      const undiscoveredGenres = excludeBadGenres(mysterySong.genre).filter(
        g => !discoveredAttributes.genre.includes(g)
      );
      
      if (undiscoveredGenres.length === 0) return;
      
      const idx = Math.floor(Math.random() * undiscoveredGenres.length);
      const genreToReveal = undiscoveredGenres[idx];
      
      setDiscoveredAttributes(prev => ({
        ...prev,
        genre: [...prev.genre, genreToReveal]
      }));
      
      setEndMsg(`Genre revealed: ${genreToReveal}`);
    } else if (hintType === 'year') {
      if (!mysterySong || !mysterySong.release_date) return;
      
      setDiscoveredAttributes(prev => ({
        ...prev,
        year: true
      }));
      
      const year = new Date(mysterySong.release_date).getFullYear();
      setEndMsg(`Year released: ${year}`);
    } else if (hintType === 'artist') {
      if (!mysterySong || !mysterySong.artist) return;
      
      setDiscoveredAttributes(prev => ({
        ...prev,
        artist: true
      }));
      
      setEndMsg(`Artist: ${mysterySong.artist}`);
    }
  }, [
    dashboardSongs, mysterySongId, setDashboardSongs,
    setSelectedWordInfo, mysterySong, discoveredAttributes, setDiscoveredAttributes,
    setEndMsg, setAssistScore, setShowHintModal]);

  const calculateWordDiscoveryBreakdown = useCallback(() => {
    if (!mysterySong) return [];

    const totalWords = mysterySong.words.length;
    const thresholds = [
      { percentage: 50, emoji: '🥶' },
      { percentage: 80, emoji: '😐' },
      { percentage: 95, emoji: '🥵' },
      { percentage: 100, emoji: '🎯' }
    ];

    return thresholds.map(threshold => {
      const targetWords = Math.ceil(totalWords * threshold.percentage / 100);
      
      const guessWhenReached = wordDiscoveryHistory.find(
        entry => entry.wordsFound >= targetWords
      );
      
      return {
        percentage: threshold.percentage,
        emoji: threshold.emoji,
        guesses: guessWhenReached ? guessWhenReached.guessNumber : guessCount
      };
    });
  }, [mysterySong, wordDiscoveryHistory, guessCount]);

  const handlePlayAgain = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: "env(safe-area-inset-top, 0px)",
        bottom: "env(safe-area-inset-bottom, 0px)",
        left: "env(safe-area-inset-left, 0px)",
        right: "env(safe-area-inset-right, 0px)",
        width: "auto",
        height: "auto",
        minWidth: 0,
        minHeight: 0,
        background: "rgba(0,0,0,0.75)",
        overflow: "hidden",
        boxSizing: "border-box",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    >

      <div
        ref={titleRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'absolute',
          top: `calc(20px + env(safe-area-inset-top, 0px))`,
          left: 20,
          zIndex: 10,
          padding: isTitleCollapsed ? 0 : 12,
          background: "rgba(0,0,0,0.8)",
          color: '#333',
          border: isTitleCollapsed ? 'none' : '2px solid #ddd',
          borderRadius: '8px',
          fontFamily: "'Sniglet', sans-serif",
          fontWeight: 400,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: isTitleCollapsed 
            ? '60px' 
            : dwidth < 450 
              ? `${Math.max(Math.min(dwidth, window.innerWidth) - 20 - 12 - 2 - 2 - 12 - 20, 200)}px`
              : 'unset',
          height: isTitleCollapsed ? '60px' : 'unset',
          transition: 'width 0.1s cubic-bezier(.4,1,.3,1), height 0.1s cubic-bezier(.4,1,.3,1), padding 0.1s cubic-bezier(.4,1,.3,1), border 0.1s cubic-bezier(.4,1,.3,1)',
        }}
      >
        {isTitleCollapsed ? (
          <button
            onClick={() => setIsTitleCollapsed(false)}
            title="Expand"
            style={{
              background: "linear-gradient(90deg,#89CFF0,#69b3e7)",
              border: "none",
              borderRadius: "8px",
              fontFamily: "'Sniglet', sans-serif",
              fontSize: "60px",
              color: "#333",
              width: "60px",
              height: "60px",
              margin: "0 auto",
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
              transition: "transform 0.15s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            L
          </button>
        ) : (
          <>
            <span
              style={{
                fontFamily: "'Sniglet', sans-serif",
                fontWeight: 400,
                fontSize: Math.min(
                  width < 375 ? (width * 0.082) : (width < 900 ? (width * 0.086) : 36),
                  36
                ),
                color: 'white',
                margin: '3px 12px 12px 12px',
                letterSpacing: "0.03em",
              }}
            >
              <strong>LyricaL</strong> <small><small><i>(the game)</i></small></small>
            </span>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                marginTop: 12,
                marginLeft: 3,
                marginRight: 3
              }}
            >
              <SongSearch
                selectedSongId={selectedSongId}
                setSelectedSongId={setSelectedSongId}
                query={searchQuery}
                setQuery={setSearchQuery}
                selectedSongText={selectedSongText}
                setSelectedSongText={setSelectedSongText}
                isGameWon={isGameWon}
              />
              <button
                onClick={e => {
                  e.preventDefault();
                  handleAddSong();
                }}
                disabled={!selectedSongId || isGameWon}
                title={selectedSongId ? "Guess Song" : "Search a song first"}
                style={{
                    marginLeft: 8,
                    background: selectedSongId && !isGameWon ? REG_BLUE : "#666",
                    color: selectedSongId && !isGameWon ? "black" : "#999",
                    borderRadius: 12,
                    border: `1px solid ${selectedSongId && !isGameWon ? "#348FD9" : "#666"}`,
                    width: 60,
                    height: 30,
                    fontFamily: "'Sniglet', sans-serif",
                    fontSize: 16,
                    fontWeight: 400,
                    cursor: selectedSongId && !isGameWon ? "pointer" : "not-allowed",
                    opacity: selectedSongId && !isGameWon ? 1 : 0.6,
                    transition: "all 0.2s ease",
                }}
              >
                Guess
              </button>
            </div>

            {loading && (
              <div
                style={{
                  padding: "24px 0 9px 14px",
                  color: REG_BLUE,
                  fontSize: 13,
                  background: "transparent",
                  textAlign: "left",
                }}
              >
                {loadingMsg || "Loading..."}
              </div>
            )}
            {!loading && (
              <div
                style={{
                  width: "87.5%",
                  margin: "24px 0px 9px 14px",
                  fontSize: 13,
                  color: REG_BLUE,
                  whiteSpace: "pre-wrap",
                  left: 0,
                }}
              >
                {endMsg}
              </div>
            )}

            <span
              onClick={() => setIsTitleCollapsed(true)}
              style={{
                position: "absolute",
                right: "6px",
                bottom: "6px",
                background: "rgba(50,50,50,0.85)",
                border: "none",
                borderRadius: "7px",
                padding: "0px 8px",
                fontSize: "18px",
                fontFamily: 'Sniglet',
                fontWeight: 800,
                color: REG_BLUE,
                cursor: "pointer",
                transition: "background 0.18s, color 0.15s, box-shadow 0.16s",
                boxShadow: "none",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "#444";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "rgba(50,50,50,0.85)";
                e.currentTarget.style.color = REG_BLUE;
              }}
              title="Collapse"
            >
              ⇱
            </span>
          </>
        )}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: width < 900 && width/height < 1.5 && !isDashboardCollapsed 
            ? 'calc(20px + 40vh)' 
            : '20px',
          left: 20,
          zIndex: 9,
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          transition: 'all 0.1s cubic-bezier(.4,2,.3,1)',
        }}
      >
        <button
          ref={menuButtonRef}
          title="More Information"
          onClick={(e) => {
            e.stopPropagation();
            handleMenuToggle();
          }}
          style={{
            fontSize: '18px',
            padding: '12px 16px',
            backgroundColor: 'rgba(0,0,0, 0.8)',
            color: 'white',
            border: '1.5px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            fontFamily: "'Sniglet', sans-serif",
            fontWeight: 400,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            height: 'auto',
            minHeight: '44px',
          }}
        >
          ☰ Menu
        </button>

        <button
          title="Share"
          onClick={(e) => {
            e.stopPropagation();
            
            if (isGameWon) {
              const shareText = `🎵 LyricaL - Daily Song\n\n` +
                `✅ Guessed in ${guessCount} ${guessCount === 1 ? 'try' : 'tries'}\n` +
                (assistScore > 0 ? `🆘 Hint Points Used: ${assistScore}\n` : `🆘 0 Hints Used!\n`) +
                `\nWord Count Progress:\n` +
                calculateWordDiscoveryBreakdown().map(item => 
                  `${item.emoji} ${item.percentage}% - ${item.guesses} ${item.guesses === 1 ? 'guess' : 'guesses'}`
                ).join('\n') +
                `\n\nhttps://reilly.fun/lyrical`;
              
              if (navigator.share) {
                navigator.share({ text: shareText }).catch((error) => {
                  if (error.name !== 'AbortError') {
                    console.log('Share failed:', error);
                  }
                });
              } else {
                navigator.clipboard.writeText(shareText).then(() => {
                  alert('Results copied to clipboard!');
                });
              }
            } else {
              const shareData = {
                text: `Check out LyricaL – it's like Wordle but for song lyrics :)`,
                url: 'https://reilly.fun/lyrical'
              };
              
              if (navigator.share) {
                navigator.share(shareData).catch((error) => {
                  if (error.name !== 'AbortError') {
                    console.log('Share failed:', error);
                  }
                });
              } else {
                console.log('Sharing not supported on this device.');
              }
            }
          }}
          style={{
            fontSize: '18px',
            padding: '12px 16px',
            backgroundColor: 'rgba(0,0,0, 0.8)',
            color: 'white',
            border: '1.5px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            fontFamily: "'Sniglet', sans-serif",
            fontWeight: 400,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            height: 'auto',
            minHeight: '44px',
          }}
        >
          <span
            style={{
              fontSize: '18.5px',
              display: 'inline-block',
              transform: 'rotate(90deg)',
              verticalAlign: 'middle'
            }}
          >
            ⎋
          </span>
          {' '}&nbsp;Share
</button>
      </div>

      {isMenuOpen && createPortal(
        <div 
          className="menu-overlay"
          onClick={handleMenuOutsideClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: width,
            height: height,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div style={{
            backgroundColor: DEEP_BLUE,
            opacity: 0.95,
            borderRadius: '12px',
            minWidth: width * 0.2,
            width: 'auto',
            maxWidth: width * 0.9,
            maxHeight: 'calc(90vh - 100px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            position: 'relative',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 20px 0 20px',
              borderBottom: '2px solid #555555'
            }}>
              <div style={{
                display: 'flex',
                gap: '0px',
                fontFamily: "'Sniglet', sans-serif",
                fontSize: width < 900 ? Math.min(18, width * 0.03) : 18,
              }}>
                {['Controls', 'About', '???'].map((tab, index) => (
                  <React.Fragment key={tab}>
                    <span
                      onClick={() => handleTabClick(tab)}
                      style={{
                        cursor: 'pointer',
                        color: activeTab === tab ? 'white' : '#a9a9a9',
                        fontWeight: 400,
                        transition: 'color 0.2s ease',
                      }}
                      onMouseEnter={(e) => e.target.style.color = 'white'}
                      onMouseLeave={(e) => e.target.style.color = activeTab === tab ? 'white' : '#a9a9a9'}
                    >
                      {tab}
                    </span>
                    {index < 2 && <span style={{ color: '#a9a9a9', margin: '0 12px' }}>|</span>}
                  </React.Fragment>
                ))}
              </div>
              <span
                onClick={() => setIsMenuOpen(false)}
                style={{
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#a9a9a9',
                  transition: 'color 0.2s ease',
                  padding: '4px',
                  marginLeft: '32px',
                }}
                onMouseEnter={(e) => e.target.style.color = 'white'}
                onMouseLeave={(e) => e.target.style.color = '#a9a9a9'}
              >
                ×
              </span>
            </div>

            <div
              id="tab-content"
              style={{
                padding: '20px',
                maxHeight: 'calc(90vh - 100px)',
                overflowY: 'auto'
              }}
            >
              {activeTab === 'Controls' && (
                <div
                  id="controls-section"
                  style={{
                    fontFamily: "'Sniglet', sans-serif",
                    fontWeight: 400,
                  }}
                >
                  {!isMobile ? (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Left click + drag</span>
                        <span style={{ color: '#a9a9a9' }}>Rotate</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Scroll wheel</span>
                        <span style={{ color: '#a9a9a9' }}>Zoom</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Right click + drag</span>
                        <span style={{ color: '#a9a9a9' }}>Pan</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Double click</span>
                        <span style={{ color: '#a9a9a9' }}>Reset zoom</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Swipe</span>
                        <span style={{ color: '#a9a9a9' }}>Rotate</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Pinch</span>
                        <span style={{ color: '#a9a9a9' }}>Zoom</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Two-finger swipe</span>
                        <span style={{ color: '#a9a9a9' }}>Pan</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.75px solid #a9a9a9' }}>
                        <span style={{ color: 'white' }}>Double tap</span>
                        <span style={{ color: '#a9a9a9' }}>Reset zoom</span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => navigate('/')}
                    style={{
                      fontSize: '16px',
                      fontFamily: "'Sniglet', sans-serif",
                      fontWeight: 400,
                      padding: '10px 20px',
                      backgroundColor: QUIT_RED,
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      width: 'fit-content',
                      margin: '20px auto 0 auto',
                      display: 'block',
                    }}
                  >
                    Quit
                  </button>
                </div>
              )}

              {activeTab === 'About' && (
                <div 
                  id="about-section"
                  style={{
                    fontFamily: "'Sniglet', sans-serif",
                    fontSize: '16px',
                    lineHeight: '1.6',
                    whiteSpace: 'normal',
                    maxWidth: '520px',
                    padding: "10px 20px 20px 20px",
                    overflowY: 'visible',
                    color: 'white',
                  }}
                >
                  <p>
                    Welcome to <strong>LyricaL</strong> – the game!
                    <br /><br />
                    Every day, there's a mystery song waiting to be guessed. Make guesses by searching 
                    for any song and clicking <i>Guess</i>. If any sections, genres, or other statistics
                    of your guessed song match those of the mystery song, they'll show up in the Dashboard.
                    <br /><br />
                    Any words that match between your guessed song and the mystery song will appear as colored 
                    spheres on the 3D map, colored by section (if you've discovered the section). Words that don't
                    match will disappear with your next guess, but any words (or other statistics) that you've
                    discovered throughout your guesses will remain. Mystery song words begin as yellow spheres.
                    <br /><br />
                    The closer two words are in the map, the closer they are in meaning. The size
                    of each word has been scaled to its frequency in Wikipedia pages – the larger
                    a word appears, the rarer it is. This makes the interesting words stand out.
                    <br /><br />
                    You can zoom to anywhere in the map you'd like! Use the Dashboard to change the colors
                    of the words in a song or song section. Click on a word in the map to see how many
                    songs / song sections share that word.
                    <br /><br />
                    After each guess, you can also see how many words matched between your guess and the
                    mystery song, as well as how many total words of the mystery song you've discovered so far.
                    <br /><br />
                    If you're stuck, the <i>Get Hint</i> button in the Dashboard will reveal different types
                    of clues to help you solve the puzzle. Each hint type adds to your Assist Score.
                    <br /><br />
                    You get unlimited guesses – good luck!
                    <br /><br />
                    --
                    <br /><br />
                    Under the hood, this game uses word vectorization, which quantifies the
                    meaning of words by ranking where they stand on hundreds of different dimensions.
                    You can find more information from the 'About' section in{' '}
                    <a 
                    href="https://reilly.fun/lyrics" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: "#69b3e7", fontFamily: "'Fredoka One', cursive" }}
                    >
                    Lyric Explorer
                    </a>, which is LyricaL's companion app that lets you visualize the lyrics of as many
                    songs as you'd like, including your own uploaded texts, all on the same canvas.
                    <br /><br />
                    For more little creations, check out{' '}
                    <a 
                    href="https://reilly.fun" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: "#69b3e7", fontFamily: "'Fredoka One', cursive" }}
                    >
                    reilly.fun
                    </a> 🤠
                  </p>
                </div>
              )}

              {activeTab === '???' && (
                <div style={{
                  fontFamily: "'Sniglet', sans-serif",
                  textAlign: 'center',
                  color: '#a9a9a9',
                  fontSize: '16px',
                  padding: '40px 0',
                }}>
                  <p>Let me know what else you'd like to see!</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {!loading && !guessedSong && firstTime && (
        <div
          style={{
            position: "absolute",
            top: centerY !== -1 ? `${centerY}px` : "50%",
            left: centerY !== -1 ? "50%" : "75%",
            transform: "translate(-50%, -50%)",
            color: "white",
            fontSize: 22,
            textAlign: "center",
            fontFamily: "'Sniglet', sans-serif",
            fontWeight: 400,
            zIndex: 8,
            pointerEvents: "none",
            width: centerY !== -1 ? "90vw": "40vw",
            maxWidth: 400,
            lineHeight: 1.5,
            transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
          }}
        >
          <PulseText speed={0.5}>
            <div>
              Guess today's mystery song!
            </div>
            <div style={{ marginTop: '0.6em' }}>
              Click the <i>Menu</i> button for more info.
            </div>
          </PulseText>
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "absolute",
            top: centerY !== -1 ? `${centerY}px` : "50%",
            left: centerY !== -1 ? "50%" : "75%",
            transform: "translate(-50%, -50%)",
            zIndex: 8,
            pointerEvents: "none",
            width: centerY !== -1 ? "90vw": "40vw",
            maxWidth: 400,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transition: 'all 0.3s cubic-bezier(.4,2,.3,1)',
          }}
        >
          <div className="orbit-spinner-bg">
            <div className="orbit-container">
              <img src="figure8.png" alt="Figure-8" className="spinning-image" />
            </div>
            <div className="orbit-container">
              <img src="figure8-rotated.png" alt="Figure-8" className="spinning-image2" />
            </div>
          </div>
        </div>
      )}

      {showHintModal && (
        <HintSelectionModal
          onClose={() => setShowHintModal(false)}
          onSelectHint={handleSelectHint}
          assistScore={assistScore}
        />
      )}

      {showWinScreen && (
        <WinScreenModal
          onClose={() => setShowWinScreen(false)}
          guessCount={guessCount}
          assistScore={assistScore}
          mysterySong={mysterySong}
          wordDiscoveryBreakdown={calculateWordDiscoveryBreakdown()}
          onPlayAgain={handlePlayAgain}
        />
      )}

      <Canvas
        id="orbit-canvas"
        camera={{ fov: 70 }}
        onDoubleClick={handleReset}
        onPointerMissed={handleCanvasClick}
        style={{
          position: 'fixed',
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100vw',
          background: 'black',
          touchAction: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      >
        <ambientLight intensity={0.8} />
        <directionalLight position={[0, 10, 5]} intensity={0.3} />
        
        <ScreenPositionTracker 
          selectedWordInfo={selectedWordInfo}
          onPositionUpdate={setInfoBoxPosition}
        />
        
        <CameraController
          wordsLength={aggregatedWords.length}
          aggregatedPositions={aggregatedPositions}
          controlsRef={controlsRef}
          resetTrigger={resetCameraTrigger}
          onResetComplete={handleResetComplete}
        />
        
        {aggregatedWords.length !== 0 && !firstTime && (
          <RotatingCloud
            words={aggregatedWords}
            positions={aggregatedPositions}
            colors={aggregatedColors}
            sections={aggregatedSections}
            radii={aggregatedRadii}
            wikiFreqs={aggregatedWikiFreqs}
            hasLabels={aggregatedHasLabels}
            selectedWordInfo={selectedWordInfo}
            onWordClick={handleWordClick}
            groupRef={groupRef}
            autoRotate={autoRotate}
          />
        )}
        
        <OrbitControls
          ref={controlsRef}
          enablePan
          enableRotate
          enableZoom
          onStart={handleDragStart}
        />
      </Canvas>

      {selectedWordInfo && selectedWordInfo.colors && (
        <div
          ref={infoBoxRef}
          style={{
            position: "absolute",
            left: `${infoBoxPosition.x + 0}px`,
            top: `${infoBoxPosition.y - 25}px`,
            transform: "translate(-50%, -100%)",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            border: `1.5px solid ${REG_BLUE}`,
            borderRadius: "12px",
            padding: "12px 16px",
            color: "white",
            fontFamily: "'Nunito', sans-serif",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
            zIndex: 15,
            width: "200px",
            minHeight: "50px",
            pointerEvents: "none",
            transition: "none",
          }}
        >
          <div style={{ 
            fontSize: "18px", 
            fontWeight: "bold", 
            marginBottom: "8px",
            ...(() => {
              const currentColors = [];
              
              const mysterySongEntry = dashboardSongs.find(song => song.id === mysterySongId);
              if (mysterySongEntry) {
                const mysteryMatches = mysterySongEntry.words.filter(w => 
                  w.word.toLowerCase() === selectedWordInfo.word.toLowerCase()
                );
                mysteryMatches.forEach(w => {
                  const color = mysterySongEntry.sectionColors[w.section];
                  if (color && !currentColors.includes(color)) {
                    currentColors.push(color);
                  }
                });
              }
              
              const guessedSongEntry = dashboardSongs.find(song => song.id !== mysterySongId);
              if (guessedSongEntry) {
                const guessedMatches = guessedSongEntry.words.filter(w => 
                  w.word.toLowerCase() === selectedWordInfo.word.toLowerCase()
                );
                guessedMatches.forEach(w => {
                  const color = guessedSongEntry.sectionColors[w.section];
                  if (color && !currentColors.includes(color)) {
                    currentColors.push(color);
                  }
                });
              }
              
              const textColorInfo = getTextColorInfo(currentColors.length > 0 ? currentColors : ['#fff']);
              return textColorInfo.isMultiColor ? textColorInfo.gradientStyle : { color: textColorInfo.color };
            })()
          }}>
            {selectedWordInfo.word}
          </div>

          {selectedWordInfo.songOccurrences && selectedWordInfo.songOccurrences.length > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <div style={{ 
                fontSize: "13px", 
                fontWeight: "bold",
                color: "#ccc",
                marginBottom: "4px"
              }}>
                Song Occurrences:
              </div>
              <ul style={{ 
                margin: 0, 
                paddingLeft: "18px",
                fontSize: "12px",
                color: "#ddd"
              }}>
                {selectedWordInfo.songOccurrences.map((occurrence, index) => (
                  <li key={index} style={{ marginBottom: "2px" }}>
                    {occurrence.songName} (
                      {occurrence.sections.map((section, sectionIndex) => {
                        const songEntry = dashboardSongs.find(s => s.name === occurrence.songName || (occurrence.songName === "???" && s.id === mysterySongId));
                        const currentColor = songEntry?.sectionColors[section.name] || section.color;
                        
                        return (
                          <React.Fragment key={sectionIndex}>
                            <span style={{ 
                              color: currentColor === "transparent" || !currentColor ? "#fff" : currentColor
                            }}>
                              {section.name}
                            </span>
                            {sectionIndex < occurrence.sections.length - 1 && ", "}
                          </React.Fragment>
                        );
                      })}
                    )
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div>
            <div style={{ 
              fontSize: "13px", 
              fontWeight: "bold",
              color: "#ccc",
              marginBottom: "4px"
            }}>
              Wiki Occurrences:
            </div>
            <ul style={{ 
              margin: 0, 
              paddingLeft: "18px",
              fontSize: "12px",
              color: "#ddd"
            }}>
              <li>
                {selectedWordInfo.isUndiscovered ? 
                  "???" : 
                  `${(selectedWordInfo.wikiFreq || 0).toLocaleString('en-US')} (${getWikiFrequencyLabel(selectedWordInfo.wikiFreq || 0)})`
                }
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function RotatingCloud({ 
  groupRef, 
  autoRotate, 
  words, 
  positions, 
  colors, 
  sections, 
  radii,
  wikiFreqs,
  hasLabels,
  selectedWordInfo,
  onWordClick
}) {

  useFrame((_, delta) => {
    if (autoRotate && groupRef.current) {
      groupRef.current.rotation.y += delta * 0.0;
    }
  });

  const positionsAreClose = (pos1, pos2, epsilon = 0.001) => {
    if (!pos1 || !pos2) return false;
    for (let i = 0; i < 3; i++) {
      if (Math.abs(pos1[i] - pos2[i]) > epsilon) return false;
    }
    return true;
  };

  return (
    <group ref={groupRef}>
      {words && words.length > 0 && words.map((word, i) => {
        const isSelected = selectedWordInfo && 
                          selectedWordInfo.word === word && 
                          positionsAreClose(selectedWordInfo.position, positions[i]);
        
        return (
          <WordPoint
            key={`${word}-${i}`}
            position={positions[i]}
            text={hasLabels[i] ? word : ""} 
            colors={colors[i]}
            section={sections[i]}
            radius={radii[i]}
            wikiFreq={wikiFreqs[i]}
            isSelected={isSelected}
            onWordClick={onWordClick}
          />
        );
      })}
    </group>
  );
}

function WordPoint({
  position = [0, 0, 0],
  text,
  colors,
  radius = 0.18,
  wikiFreq,
  isSelected = false,
  onWordClick
}) {

  const textRef = useRef();
  const { camera } = useThree();

  const validColors = (Array.isArray(colors) ? colors : [colors]).filter(c => c && c !== 'transparent');
  const isFullyTransparent = validColors.length === 0;

  const textColorInfo = getTextColorInfo(colors);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (onWordClick && !isFullyTransparent) {
      if (!text || text === "") {
        onWordClick(e, {
          word: "???",
          wikiFreq: 0,
          position: position,
          colors: colors,
          isUndiscovered: true
        });
      } else {
        onWordClick(e, {
          word: text,
          wikiFreq: wikiFreq,
          position: position,
          colors: colors
        });
      }
    }
  }, [onWordClick, isFullyTransparent, text, wikiFreq, position, colors]);

  useFrame(() => {
    if (textRef.current) {
      textRef.current.quaternion.copy(camera.quaternion);
      const right = new THREE.Vector3();
      camera.getWorldDirection(right);
      right.cross(camera.up).normalize();
      right.multiplyScalar(radius * 2);
      textRef.current.position.set(right.x, right.y, right.z);
    }
  });

  if (isFullyTransparent) {
    return null;
  }

  return (
    <group 
      position={position} 
      onClick={handleClick}
      onPointerOver={(e) => !isFullyTransparent && (document.body.style.cursor = 'pointer')}
      onPointerOut={() => (document.body.style.cursor = 'auto')}
    >
      <MultiColorSphere 
        colors={colors}
        radius={radius}
        position={[0, 0, 0]}
        isSelected={isSelected}
        text={text}
      />
      
      {text && (textColorInfo.isMultiColor ? (
        <Text
          ref={textRef}
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={isSelected ? 0.8 : 0.4}
          fontFamily="'Nunito', sans-serif"
          fontWeight={isSelected ? 'bold' : 'normal'}
          fontSize={Math.max(0.18, radius * 2)}
          anchorX="left"
          anchorY="middle"
          outlineColor="black"
          outlineWidth={0.02}
        >
          {text}
        </Text>
      ) : (
        <Text
          ref={textRef}
          color={textColorInfo.color}
          emissive={textColorInfo.color}
          emissiveIntensity={isSelected ? 1.2 : 0.6}
          fontFamily="'Nunito', sans-serif"
          fontWeight={isSelected ? 'bold' : 'normal'}
          fontSize={Math.max(0.18, radius * 2)}
          anchorX="left"
          anchorY="middle"
          outlineColor="black"
          outlineWidth={0.01}
        >
          {text}
        </Text>
      ))}
    </group>
  );
}

function MultiColorSphere({ colors, radius, position, isSelected, text }) {
  const groupRef = useRef();
  const { camera } = useThree();

  colors = Array.isArray(colors) ? colors : [colors];
  const filteredColors = colors.filter(c => c && c !== 'transparent');

  useFrame(() => {
    if (groupRef.current) {
      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);

      const upVector = new THREE.Vector3(0, 1, 0);

      const quat = new THREE.Quaternion();
      quat.setFromUnitVectors(upVector, cameraDirection.negate());
      
      groupRef.current.quaternion.slerp(quat, 0.1);
    }
  });

  if (filteredColors.length <= 1) {
    const singleColor = filteredColors[0] || "";
    if (singleColor === "") return null;

    return (
      <mesh position={position}>
        <sphereGeometry args={[radius, 20, 20]} />
        <meshStandardMaterial
          color={singleColor}
          emissive={singleColor}
          emissiveIntensity={isSelected ? 1.2 : 0.6}
        />
      </mesh>
    );
  }

  const sliceAngle = (Math.PI * 2) / filteredColors.length;

  return (
    <group ref={groupRef} position={position}>
      {filteredColors.map((color, index) => {
        if (color === "") {
          return null;
        }

        const startAngle = index * sliceAngle;

        return (
          <mesh key={`slice-${index}-${color}`}>
            <sphereGeometry
              args={[
                radius,
                20,
                20,
                startAngle,
                sliceAngle,
              ]}
            />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={isSelected ? 1.2 : 0.6}
              side={THREE.DoubleSide}
            />
          </mesh>
        );
      })}
    </group>
  );
}

function CameraController({ 
  wordsLength,
  aggregatedPositions, 
  controlsRef, 
  resetTrigger, 
  onResetComplete 
}) {
  const { camera } = useThree();
  const prevWordsLengthRef = useRef(0);
  const [pendingReset, setPendingReset] = useState(false);

  const resetCameraToPoints = useCallback(() => {
    if (aggregatedPositions.length === 0) {
      camera.position.set(...DEFAULT_CAMERA_POS);
      controlsRef.current?.target.set(0, 0, 0);
      controlsRef.current?.update();
      setPendingReset(false);
      if (onResetComplete) onResetComplete();
      return;
    }

    const centroid = [0, 0, 0];
    aggregatedPositions.forEach(pos => {
      centroid[0] += pos[0];
      centroid[1] += pos[1];
      centroid[2] += pos[2];
    });
    centroid[0] /= aggregatedPositions.length;
    centroid[1] /= aggregatedPositions.length;
    centroid[2] /= aggregatedPositions.length;

    let maxExtent = 0;
    aggregatedPositions.forEach(pos => {
      const dx = pos[0] - centroid[0];
      const dy = pos[1] - centroid[1];
      const dz = pos[2] - centroid[2];
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (distance > maxExtent) maxExtent = distance;
    });

    const zoomOut = maxExtent * 1.25 + 5;
    camera.position.set(centroid[0], centroid[1], centroid[2] + zoomOut);

    controlsRef.current?.target.set(...centroid);
    controlsRef.current?.update();

    setPendingReset(false);
    if (onResetComplete) onResetComplete();
  }, [aggregatedPositions, camera, controlsRef, onResetComplete]);

  useEffect(() => {
    if (wordsLength !== prevWordsLengthRef.current) {
      prevWordsLengthRef.current = wordsLength;
      setPendingReset(true);
    }
  }, [wordsLength]);

  useEffect(() => {
    if (pendingReset && aggregatedPositions.length > 0) {
      const timeoutId = setTimeout(() => {
        resetCameraToPoints();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [pendingReset, resetCameraToPoints, aggregatedPositions.length]);

  useEffect(() => {
    if (resetTrigger) {
      resetCameraToPoints();
    }
  }, [resetTrigger, resetCameraToPoints]);

  return null;
}

function Lyrical() {
  const [dashboardSongs, setDashboardSongs] = useState([]);
  const [isDashboardCollapsed, setIsDashboardCollapsed] = useState(true);
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSongText, setSelectedSongText] = useState("");
  const [newlyAddedSongId, setNewlyAddedSongId] = useState(null);

  const [discoveredAttributes, setDiscoveredAttributes] = useState({ name: false, sections: [], artist: false, genre: [], label: false, year: false });
  const [mysterySongId, setMysterySongId] = useState(null);

  const [guessCount, setGuessCount] = useState(0);
  const [isGameWon, setIsGameWon] = useState(false);
  const [endMsg, setEndMsg] = useState("");
  const [firstTime, setFirstTime] = useState(true);

  const [selectedWordInfo, setSelectedWordInfo] = useState(null);
  
  const [assistScore, setAssistScore] = useState(0);
  const [wordDiscoveryHistory, setWordDiscoveryHistory] = useState([]);
  const [showWinScreen, setShowWinScreen] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);

  function handleSectionColorChange(songId, sectionName, newColor) {
    setDashboardSongs(current =>
      current.map(song => {
        if (song.id !== songId) return song;
        const newSectionColors = { ...song.sectionColors, [sectionName]: newColor };
        const updatedWords = song.words.map(w => w.section === sectionName ? { ...w, color: newColor } : w);
        return { ...song, sectionColors: newSectionColors, words: updatedWords, masterColor: undefined };
      })
    );
  }

  function handleMasterColorChange (songId, newMasterColor) {
    setDashboardSongs(current =>
      current.map(song => {
        if (song.id !== songId) return song;
        const newSectionColors = {};
        Object.keys(song.sections).forEach(section => {
          newSectionColors[section] = newMasterColor;
        });
        const updatedWords = song.words.map(w => ({ ...w, color: newMasterColor }));
        return { ...song, masterColor: newMasterColor, sectionColors: newSectionColors, words: updatedWords };
      })
    );
  }

  const handleDeleteSong = useCallback((songId) => {
    setDashboardSongs(current => current.filter(song => song.id !== songId));
  }, [setDashboardSongs]);

  const handleGetHint = useCallback(() => {
    setDashboardSongs(prev => {
      const currentMysterySong = prev.find(song => song.id === mysterySongId);
      if (!currentMysterySong) return prev;
      
      const undiscoveredGenres = currentMysterySong.genre ? 
        excludeBadGenres(currentMysterySong.genre).filter(g => !discoveredAttributes.genre.includes(g)) : [];
      
      const canRevealWord = currentMysterySong.words.some(w => !w.hasLabel);
      const canRevealGenre = undiscoveredGenres.length > 0;
      const canRevealYear = !discoveredAttributes.year && currentMysterySong.release_date;
      const canRevealArtist = !discoveredAttributes.artist && currentMysterySong.artist;
      
      if (!canRevealWord && !canRevealGenre && !canRevealYear && !canRevealArtist) {
        return prev;
      }
      
      return prev;
    });
    
    setShowHintModal(true);
  }, [mysterySongId, discoveredAttributes]);

  return (
    <>
      <MusicViz
        dashboardSongs={dashboardSongs}
        setDashboardSongs={setDashboardSongs}
        selectedSongId={selectedSongId}
        setSelectedSongId={setSelectedSongId}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedSongText={selectedSongText}
        setSelectedSongText={setSelectedSongText}
        newlyAddedSongId={newlyAddedSongId}
        setNewlyAddedSongId={setNewlyAddedSongId}
        isDashboardCollapsed={isDashboardCollapsed}
        setIsDashboardCollapsed={setIsDashboardCollapsed}
        discoveredAttributes={discoveredAttributes}
        setDiscoveredAttributes={setDiscoveredAttributes}
        mysterySongId={mysterySongId}
        setMysterySongId={setMysterySongId}
        guessCount={guessCount}
        setGuessCount={setGuessCount}
        isGameWon={isGameWon}
        setIsGameWon={setIsGameWon}
        endMsg={endMsg}
        setEndMsg={setEndMsg}
        firstTime={firstTime}
        setFirstTime={setFirstTime}
        selectedWordInfo={selectedWordInfo}
        setSelectedWordInfo={setSelectedWordInfo}
        assistScore={assistScore}
        setAssistScore={setAssistScore}
        wordDiscoveryHistory={wordDiscoveryHistory}
        setWordDiscoveryHistory={setWordDiscoveryHistory}
        showWinScreen={showWinScreen}
        setShowWinScreen={setShowWinScreen}
        showHintModal={showHintModal}
        setShowHintModal={setShowHintModal}
      />
      <Dashboard
        songs={dashboardSongs}
        setSongs={setDashboardSongs}
        onSectionColorChange={handleSectionColorChange}
        onMasterColorChange={handleMasterColorChange}
        onDeleteSong={handleDeleteSong}
        onGetHint={handleGetHint}
        collapsed={isDashboardCollapsed}
        setCollapsed={setIsDashboardCollapsed}
        newlyAddedSongId={newlyAddedSongId}
        setNewlyAddedSongId={setNewlyAddedSongId}
        discoveredAttributes={discoveredAttributes}
        mysterySongId={mysterySongId}
        setMysterySongId={setMysterySongId}
        firstTime={firstTime}
        isGameWon={isGameWon}
      />
    </>
  );
}

export default Lyrical;
